import React, { createContext, useContext } from 'react';

/**
 * Tab-wise admin access (owner request 2026-10-26).
 *
 * Super admins (fixed server-side, Backend/admin_access.py) see and use every
 * group and sub-tab. Every other admin sees what a super admin enabled in
 * Admins & access → Admin tabs; new groups and sub-tabs start hidden.
 * Admins & access itself is open to every admin to look at; only super
 * admins can change anything there. The server
 * decides and enforces (403 admin_tab_not_enabled on the sections behind a
 * hidden group or tab); this file only reads its answer from GET
 * /api/v1/admin/views:
 *
 *   {success, groups: [...only the groups this admin may open...],
 *    hidden: [...their own "Customize views" choices...],
 *    super_admin: bool,
 *    tabs: access to the controlled sub-tabs, keyed "group.tab"}
 *
 * `tabs` is read leniently (a map of key → bool or {allowed}, a list of
 * allowed keys, or a list of {key, allowed}) — see parseTabs. A controlled
 * sub-tab the answer does not mention falls back to DEFAULT_HIDDEN_TABS for a
 * non-super admin. An answer with neither `super_admin` nor `tabs` comes from
 * a server without access control: everything stays as it was (`legacy`).
 *
 * Panels ask useAdminAccess().canTab(group, tabKey(name)) for their sub-tabs;
 * a tab nobody controls is always allowed.
 */

/** The group the shell adds to the bottom of the nav for every admin (team
 *  accounts included): everyone can look; only super admins can change
 *  anything there, and the server refuses everyone else's changes. */
export const ACCESS_GROUP = {
  key: 'access',
  title: 'Admins & access',
  description: 'Admin and read-only team accounts, who added them, and which groups each admin can see',
};

/** The fixed super admins (Backend/admin_access.py SUPER_ADMINS), for display
 *  only: marked in Access control and left out of the admin pickers. Access
 *  itself is always the server's answer. access_state.super_admins, when the
 *  server sends it, takes precedence. */
export const KNOWN_SUPER_ADMINS = ['pranavgarg37@gmail.com', 'pranavgarg64@gmail.com'];

/** Controlled sub-tabs hidden from other admins until a super admin enables
 *  them — the fallback when the server's answer does not mention one. */
export const DEFAULT_HIDDEN_TABS = ['ocean.cosmos_matching', 'ocean.search_engine'];

/** Sub-tabs only super admins can ever open (never delegated, not listed in
 *  Access control): the Feature access tabs of For You & What's New and Ocean,
 *  where a super admin decides who may open those member pages. Other admins
 *  never see them, whatever their access to the group; a deep link shows them
 *  "not enabled". */
export const SUPER_ONLY_TABS = ['foryou_whatsnew.feature_access', 'ocean.feature_access'];

/** "COSMOS matching" -> "cosmos_matching": the deep-link / access key of a tab name. */
export const tabKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

export interface AdminAccess {
  /** No access control on this server: everything allowed, no Access control page. */
  legacy: boolean;
  superAdmin: boolean;
  /** Group keys this admin may open (personal hidden ones included). Empty = not loaded. */
  groups: Set<string>;
  /** Explicit answers for controlled sub-tabs, "group.tab" -> allowed. */
  tabs: Record<string, boolean>;
  /** 'team' = read-only team account (Backend/admin_accounts.py): every change is refused. */
  role: 'admin' | 'team';
}

export const OPEN_ACCESS: AdminAccess = {
  legacy: true, superAdmin: false, groups: new Set(), tabs: {}, role: 'admin',
};

export function canGroup(a: AdminAccess, group: string): boolean {
  if (a.legacy || a.superAdmin || group === ACCESS_GROUP.key) return true;
  return a.groups.has(group);
}

export function canTab(a: AdminAccess, group: string, tab: string): boolean {
  const key = `${group}.${tab}`;
  if (a.superAdmin) return true;
  if (SUPER_ONLY_TABS.includes(key)) return false;
  if (a.legacy) return true;
  if (!canGroup(a, group)) return false;
  if (key in a.tabs) return a.tabs[key];
  return !DEFAULT_HIDDEN_TABS.includes(key);
}

const allowedFlag = (v: any): boolean | null => {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    if (['all', 'allowed', 'visible', 'enabled', 'true', 'yes'].includes(v)) return true;
    if (['none', 'hidden', 'denied', 'false', 'no'].includes(v)) return false;
    return null;
  }
  if (v && typeof v === 'object') {
    for (const k of ['allowed', 'can', 'visible', 'enabled', 'access']) {
      if (typeof v[k] === 'boolean') return v[k];
    }
  }
  return null;
};

/** The server's `tabs` in any of the shapes it may take. */
export function parseTabs(raw: any): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  if (!raw) return out;
  if (Array.isArray(raw)) {
    raw.forEach((it: any) => {
      if (typeof it === 'string') { if (it.includes('.')) out[it] = true; return; }
      if (!it || typeof it !== 'object') return;
      const key = typeof it.key === 'string' && it.key.includes('.') ? it.key
        : (typeof it.group === 'string' && typeof it.tab === 'string' ? `${it.group}.${it.tab}` : '');
      const ok = allowedFlag(it);
      if (key && ok !== null) out[key] = ok;
    });
    return out;
  }
  if (typeof raw !== 'object') return out;
  Object.entries(raw).forEach(([k, v]) => {
    if (k.includes('.')) {
      const ok = allowedFlag(v);
      if (ok !== null) out[k] = ok;
      return;
    }
    // {group: {tab: bool}} or {group: [allowed tabs]}
    if (Array.isArray(v)) v.forEach(t => { if (typeof t === 'string') out[`${k}.${t}`] = true; });
    else if (v && typeof v === 'object') {
      Object.entries(v as Record<string, any>).forEach(([t, tv]) => {
        const ok = allowedFlag(tv);
        if (ok !== null) out[`${k}.${t}`] = ok;
      });
    }
  });
  return out;
}

export interface ViewGroupLite { key: string; title: string; description: string }

/** GET admin/views -> the groups to show plus this admin's access. */
export function parseViews(data: any): { groups: ViewGroupLite[]; hidden: string[]; access: AdminAccess } {
  const d = data && typeof data === 'object' ? data : {};
  const groups: ViewGroupLite[] = (Array.isArray(d.groups) ? d.groups : [])
    .filter((g: any) => g && typeof g.key === 'string')
    .map((g: any) => ({ key: g.key, title: String(g.title || g.key), description: String(g.description || '') }));
  const hidden: string[] = (Array.isArray(d.hidden) ? d.hidden : []).filter((x: any) => typeof x === 'string');
  const legacy = !('super_admin' in d) && !('tabs' in d);
  const access: AdminAccess = {
    legacy,
    superAdmin: d.super_admin === true,
    groups: new Set(groups.map(g => g.key)),
    tabs: parseTabs(d.tabs),
    role: d.role === 'team' ? 'team' : 'admin',
  };
  return { groups, hidden, access };
}

const AdminAccessContext = createContext<AdminAccess>(OPEN_ACCESS);
export const AdminAccessProvider = AdminAccessContext.Provider;

/** This admin's access, with the two checks bound to it. Outside the admin
 *  shell everything is allowed (the server still enforces). */
export function useAdminAccess() {
  const a = useContext(AdminAccessContext);
  return {
    ...a,
    canGroup: (group: string) => canGroup(a, group),
    canTab: (group: string, tab: string) => canTab(a, group, tab),
  };
}

/** Panels and the shell fire this (window) when access may have changed: a
 *  super admin saved a rule, or the server answered 403 admin_tab_not_enabled. */
export { ADMIN_ACCESS_CHANGED_EVENT } from '../../services/apiService';

/** The clear "not for you" state for a hidden group or sub-tab reached by a
 *  deep link, a bookmark or an old tab. */
export const NotEnabled: React.FC<{ what?: string; compact?: boolean }> = ({ what, compact }) => (
  <div role="status"
    className={`rounded-xl border border-grayscale-300 bg-white text-center ${compact ? 'p-6' : 'p-10'}`}>
    <div className="mx-auto mb-3 w-10 h-10 rounded-full bg-grayscale-200 flex items-center justify-center text-grayscale-1100"
      aria-hidden>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        strokeLinecap="round" strokeLinejoin="round">
        <rect x="4" y="11" width="16" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </svg>
    </div>
    <div className="text-sm font-bold text-grayscale-1300 font-grotesk mb-1">
      {what ? `${what} is not enabled for your account` : 'Not enabled for your account'}
    </div>
    <p className="text-sm text-grayscale-1200">
      Ask a super admin to enable it for you.
    </p>
  </div>
);
